/**
 * Withdraw page.
 * URL: /withdraw
 *
 * Endpoints:
 *   GET  /api/users/balances — { balances: Record<string, string> }
 *   GET  /api/users/profile  — { wallet_address?: string, security_question?: string, ... }
 *   POST /api/withdraw       — { amount, wallet_address, transaction_password, security_answer }
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/Button';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedBottomActionBar, ThemedInput, ThemedField, ThemedNotice, ThemedPanel } from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { WALLET_COLORS, CASHFLOW_COLORS as C } from '@/constants';

// ---- Types ------------------------------------------------------------------

interface BalancesResponse {
  balances: Record<string, string>;
}

interface ProfileResponse {
  wallet_address?: string;
  security_question?: string;
  [key: string]: unknown;
}

interface WithdrawBody {
  amount: string;
  wallet_address: string;
  transaction_password: string;
  security_answer?: string;
}

// ---- Constants --------------------------------------------------------------

const WITHDRAW_FEE_RATE = 0.03; // 3%
const MIN_WITHDRAW = 100;

export function ChevronIcon({ up }: { up?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} className={up ? 'rotate-180' : ''}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}



// ---- Component --------------------------------------------------------------

export function WithdrawPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);

  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Balances
  const { data: balData } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
    refetchInterval: 30_000,
  });

  // Profile (for bound wallet + security question)
  const { data: profileData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => usersAPI.getProfile() as Promise<ProfileResponse>,
  });

  const withdrawableRaw =
    balData?.balances?.['withdrawable'] ??
    balData?.balances?.['uo'] ??
    '0';
  const withdrawableBalance = Number(withdrawableRaw);

  const boundWallet = profileData?.wallet_address ?? '';
  const securityQuestion =
    profileData?.security_question ??
    (profile as Record<string, unknown> | null)?.['security_question'] as string | undefined ??
    t('withdraw.defaultSecurityQuestion');

  // Pre-fill wallet address once profile loads
  useEffect(() => {
    if (boundWallet && !walletAddress) {
      setWalletAddress(boundWallet);
    }
  }, [boundWallet, walletAddress]);

  // Computed values
  const amountNum = Number(amount) || 0;
  const feePercent = WITHDRAW_FEE_RATE * 100;
  const receiveAmount = amountNum > 0 ? Math.round(amountNum * (1 - WITHDRAW_FEE_RATE) * 100) / 100 : 0;
  const submitAddress = boundWallet || walletAddress.trim();

  function handleAll() {
    setAmount(withdrawableRaw);
  }

  function handleSubmit() {
    navigate('/uoc');
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('withdraw.title')} onBack={() => navigate(-1)} theme={C} />

        <div className="flex flex-col gap-5 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 88px)', paddingBottom: '112px' }}>
          <ThemedNotice tone="info">
            {t(
              'withdraw.deprecatedHint',
              'ORB 法币提现已停用。请使用 ORBC 转出 → AP 排队 → AP兑捐 ORB（/api/uoc/transfer-out、/api/ap/donate-uo）。',
            )}
          </ThemedNotice>
          {/* Balance card */}
          <ThemedPanel className="flex flex-col items-center gap-1 p-4" theme={C}>
            <div className="text-xs" style={{ color: C.muted }}>{t('withdraw.availableBalance')}</div>
            <div className="flex items-end gap-1">
              <span className="font-mono text-3xl font-bold leading-none" style={{ color: C.text }}>
                {withdrawableBalance.toFixed(2)}
              </span>
              <span className="text-xs leading-none mb-1" style={{ color: WALLET_COLORS.arrowKey }}>ORB</span>
            </div>
          </ThemedPanel>

          <ThemedPanel className="p-4" theme={C}>
            <div className="flex flex-col gap-4">
              {/* Wallet address */}
              <ThemedField
                label={t('withdraw.walletAddress')}
                required
                headerRight={boundWallet ? (
                  <span className="ml-auto inline-flex max-w-full min-w-0 items-center justify-end gap-1.5 text-xs leading-5" style={{ color: C.positive }}>
                    <LockIcon />
                    <span className="shrink-0">{t('withdraw.boundAddress')}</span>
                    <span className="min-w-0 truncate font-mono" style={{ color: C.text }}>{boundWallet}</span>
                  </span>
                ) : (
                  <div
                    onClick={() => navigate('/bind-wallet')}
                    className="cursor-pointer text-xs font-medium leading-none underline underline-offset-2"
                    style={{ color: C.green }}
                  >
                    {t('withdraw.bindAddress')}
                  </div>
                )}
              >
                {!boundWallet && (
                  <ThemedInput
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder={t('withdraw.addressPlaceholder')}
                  />
                )}
              </ThemedField>

              {/* Amount input */}
              <ThemedField label={t('withdraw.amount')} required>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <ThemedInput
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={t('withdraw.amountPlaceholder')}
                      className="flex-1"
                      min="0"
                      step="any"
                    />
                    <button type="button" onClick={handleAll} className="h-10 rounded-lg border px-3 text-sm font-semibold" style={{ borderColor: C.softBorder, background: C.chipBg, color: C.gold }}>
                      {t('common.all_btn')}
                    </button>
                  </div>
                  {/* Min notice */}
                  <div className="text-xs" style={{ color: C.muted }}>
                    {t('withdraw.minWithdraw', { amount: MIN_WITHDRAW })}
                  </div>
                </div>
              </ThemedField>


              {/* Transaction password + security toggle */}
              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityQuestion}
                colors={{
                  muted: C.muted,
                  red: C.red,
                  activeGreen: C.activeGreen,
                  gold: C.gold,
                  softBorder: C.softBorder,
                }}
              />
            </div>
          </ThemedPanel>

          {/* Fee + receive amount */}
          <ThemedPanel className="p-4 flex flex-col gap-2 text-sm" theme={C}>
            <div className="flex items-center justify-between">
              <span style={{ color: C.muted }}>{t('withdraw.fee')}</span>
              <span className="font-semibold" style={{ color: C.text }}>{feePercent}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: C.muted }}>{t('withdraw.actualAmount')}</span>
              <span className="font-mono font-semibold" style={{ color: C.gold }}>
                {receiveAmount > 0 ? receiveAmount.toFixed(2) : '--'} ORB
              </span>
            </div>
          </ThemedPanel>

          {/* Error */}
          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}

          {/* Success */}
          {success && <ThemedNotice tone="success">{t('withdraw.successMsg')}</ThemedNotice>}

        </div>
        <ThemedBottomActionBar background={C.bottomBarBg} className="pb-5 pt-3">
          <Button
            type="button"
            fullWidth
            onClick={handleSubmit}
            className="rounded-xl border text-sm font-medium hover:brightness-110 disabled:brightness-75"
            style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}
          >
            {t('withdraw.goUoc', '前往 ORBC 转出')}
          </Button>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
